import { useIntl } from 'react-intl';

import {
  buildSharedTooltipHeader,
  buildSharedTooltipRow,
  type SharedTooltipRow,
} from './sharedXYChartTooltipUtils.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { SeriesDisplayMode } from '../../../../core/types/tooltip.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { SeriesQueryResult } from '../../../context/XYChartOverlayPerformance.context.js';
import { useAreaData } from '../../../hooks/useAreaData.js';
import { useAxesFormatter } from '../../../hooks/useAxesFormatter.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useFormatCategory } from '../../../hooks/useFormatCategory.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartTooltip } from '../../../hooks/useXYChartTooltip.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import type { TooltipContent } from '../../../types/state.js';

type SharedTooltipCtx = Parameters<typeof buildSharedTooltipRow>[1];

type LazySharedRow = {
  name: string | string[];
  nameSegments: string[] | undefined;
  value: string;
  result: SeriesQueryResult;
};

function formatSharedTooltipValue(row: SharedTooltipRow) {
  return row.yAxisDuration
    ? `${row.yAxisValue} (${row.yAxisDuration})`
    : row.yAxisValue;
}

function LazySharedTooltipRow({
  raw,
  ctx,
  seriesDisplayMode,
}: {
  readonly raw: LazySharedRow;
  readonly ctx: SharedTooltipCtx;
  readonly seriesDisplayMode: SeriesDisplayMode | undefined;
}) {
  const row = buildSharedTooltipRow(raw.result as TooltipContent, ctx);
  const value = formatSharedTooltipValue(row);

  const isMultiLine =
    row.nameSegments !== undefined && seriesDisplayMode === 'multi-line';
  const displayName: string | string[] = isMultiLine
    ? (row.nameSegments as string[])
    : row.name;

  return (
    <OverlayTooltip.SeriesItem
      name={displayName}
      multiLine={isMultiLine}
      value={value}
      color={row.color}
      actions={row.actions}
    />
  );
}

export function SharedXYChartTooltip({
  preferred,
  datapoints,
}: Readonly<{
  preferred: TooltipContent;
  datapoints: SeriesQueryResult[];
}>) {
  const { seriesToMetadata, axisIdToScaleDomain, datapointMetadataMap } =
    useXYChartData();
  const axisIdToFormatter = useAxesFormatter();
  const intl = useIntl();
  const colorScaleMap = useXYChartColorScales();
  const { truncationMode } = useXYChartTruncationMode();
  const formatCategory = useFormatCategory();
  const { filteredSeriesWithDatapoints } = useBarData();
  const { filteredSeriesWithDatapoints: filteredAreaSeriesWithDatapoints } =
    useAreaData();
  const { seriesDisplayMode } = useXYChartTooltip();

  const ctx = {
    seriesToMetadata,
    axisIdToFormatter,
    axisIdToScaleDomain,
    datapointMetadataMap,
    formatCategory,
    intl,
    colorScaleMap,
    allBarSeries: filteredSeriesWithDatapoints,
    allAreaSeries: filteredAreaSeriesWithDatapoints,
  };

  const { xAxisValue, xAxisDuration, label } = buildSharedTooltipHeader(
    preferred,
    ctx,
  );

  const rows: LazySharedRow[] = datapoints.map((result) => {
    const metadata = safeGet(seriesToMetadata, result.series);
    const row = buildSharedTooltipRow(result as TooltipContent, ctx);

    const isMultiLine =
      row.nameSegments !== undefined && seriesDisplayMode === 'multi-line';
    const value = formatSharedTooltipValue(row);
    return {
      name: isMultiLine ? row.nameSegments : metadata.name,
      nameSegments: row.nameSegments,
      value,
      result,
    };
  });

  const activeItemIndex = datapoints.findIndex(
    (d) => d.series === preferred.series,
  );

  return (
    <OverlayTooltip truncation={truncationMode}>
      <OverlayTooltip.Header
        header1={xAxisValue}
        duration1={xAxisDuration}
        label={label}
      />
      <OverlayTooltip.Series
        data={rows}
        variant="shared"
        activeItem={activeItemIndex}
        seriesItemTemplate={(item) => (
          <LazySharedTooltipRow
            raw={item}
            ctx={ctx}
            seriesDisplayMode={seriesDisplayMode}
          />
        )}
      />
    </OverlayTooltip>
  );
}
